/**
 * Header del Call Sidebar: avatar, nombre, teléfono, timer, controles mute/hangup.
 * Visible en todas las fases (connecting, active, post_call).
 */

import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { MiniWaveform } from '@/components/ui/MiniWaveform';
import {
  PhoneXMarkIcon,
  XMarkIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/solid';
import type { CallPhase } from '@/types/callSidebar';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase() || '?';
}

// ─── Phase badge ─────────────────────────────────────────────────────────────

function PhaseBadge({ phase }: { phase: CallPhase }) {
  if (phase === 'connecting') {
    return (
      <motion.span
        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"
        animate={{ opacity: [1, 0.5, 1] }}
        transition={{ duration: 1.2, repeat: Infinity }}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
        Conectando...
      </motion.span>
    );
  }
  if (phase === 'active') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
        En llamada
      </span>
    );
  }
  if (phase === 'post_call') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
        Finalizada
      </span>
    );
  }
  return null;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface CallSidebarHeaderProps {
  contactName: string;
  contactPhone: string;
  phase: CallPhase;
  seconds: number;
  isMuted: boolean;
  remoteStream: MediaStream | null;
  onToggleMute: () => void;
  onHangup: () => void;
  onClose: () => void;
  /** Acción rápida: marcar como buzón de voz, colgar y pre-seleccionar resultado */
  onVoicemail: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CallSidebarHeader({
  contactName,
  contactPhone,
  phase,
  seconds,
  isMuted,
  remoteStream,
  onToggleMute,
  onHangup,
  onClose,
  onVoicemail,
}: CallSidebarHeaderProps) {
  const initials = getInitials(contactName);
  const isLive = phase === 'connecting' || phase === 'active';

  const handleClose = useCallback(() => {
    if (isLive) {
      const ok = window.confirm('¿Seguro que quieres cerrar? La llamada se colgará.');
      if (!ok) return;
      onHangup();
    }
    onClose();
  }, [isLive, onHangup, onClose]);

  return (
    <div className="relative px-4 pt-4 pb-3 bg-gradient-to-b from-slate-50 to-white border-b border-gray-200">
      {/* Close button */}
      <button
        type="button"
        onClick={handleClose}
        className="absolute top-3 right-3 h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
        aria-label="Cerrar"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>

      {/* Contact info */}
      <div className="flex items-center gap-3 pr-8">
        <div
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm shrink-0',
            phase === 'active' ? 'bg-green-500' : phase === 'connecting' ? 'bg-blue-500' : 'bg-gray-400',
          )}
        >
          {initials}
          {phase === 'active' && (
            <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-400 border-2 border-white animate-pulse" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">{contactName || 'Sin nombre'}</p>
          <p className="text-xs text-gray-500 font-mono">{contactPhone}</p>
        </div>
      </div>

      {/* Phase badge + Timer */}
      <div className="flex items-center justify-between mt-3">
        <PhaseBadge phase={phase} />

        <div className="flex items-center gap-2">
          {phase === 'active' && (
            <>
              <span className="text-lg font-mono font-bold text-green-600 tabular-nums">
                {formatTimer(seconds)}
              </span>
              {remoteStream && (
                <MiniWaveform stream={remoteStream} barColor="#16a34a" width={36} height={16} />
              )}
            </>
          )}
          {phase === 'post_call' && seconds > 0 && (
            <span className="text-sm font-mono text-gray-500">{formatTimer(seconds)}</span>
          )}
        </div>
      </div>

      {/* Call controls */}
      {isLive && (
        <div className="flex items-center gap-2 mt-3">
          {/* Botón silenciar micrófono */}
          <button
            type="button"
            onClick={onToggleMute}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all',
              isMuted
                ? 'bg-red-100 text-red-700 ring-1 ring-red-300 hover:bg-red-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
            aria-label={isMuted ? 'Activar micrófono' : 'Silenciar micrófono'}
          >
            <span className="relative inline-flex h-4 w-4">
              <MicrophoneIcon className={cn('h-4 w-4', isMuted && 'text-red-500')} />
              {isMuted && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="block w-[2px] h-5 bg-red-500 rotate-45 rounded-full" />
                </span>
              )}
            </span>
            {isMuted ? 'Micro OFF' : 'Silenciar'}
          </button>

          {/* Botón buzón de voz — acción rápida */}
          <button
            type="button"
            onClick={onVoicemail}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition-all"
            aria-label="Marcar como buzón de voz"
            title="Buzón de voz: cuelga y marca como buzón"
          >
            <span className="text-sm leading-none">📬</span>
            Buzón
          </button>

          {/* Botón colgar */}
          <button
            type="button"
            onClick={onHangup}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-red-500 text-white hover:bg-red-600 active:bg-red-700 transition-colors ml-auto"
            aria-label="Colgar"
          >
            <PhoneXMarkIcon className="h-4 w-4" />
            Colgar
          </button>
        </div>
      )}
    </div>
  );
}
